import type {
  AgentFeedbackSummary,
  AgentReport,
  CapabilityCuration,
  CurationQueueItem,
  OwnerAnalytics
} from '@ralph/contracts'
import { ApiRequestError, api, apiGet, apiSend } from './client.ts'

/**
 * Engagement: the feedback loop between the people who use an agent and the person who owns
 * it, plus the administrator surfaces that stand between them (PRD-18 AGM-120…123).
 *
 * The privacy split the contracts encode structurally is respected here by omission: there
 * is no client function that would let an owner read a conversation, a note's author, or a
 * reporter's name, because no such endpoint exists. The only owner-facing reads are the two
 * aggregates below. If a future endpoint hands an owner more than that, it does not belong
 * in this module — the module is the list of what the owner plane can ask for.
 *
 * // WIRE: D-api owns the routes. Envelopes this module expects:
 * //   POST   /api/messages/:messageId/feedback            {helpful, note} → 204 (or 200 {})
 * //   GET    /api/maker/agents/:agentId/feedback          → AgentFeedbackSummary
 * //   GET    /api/maker/agents/:agentId/analytics         → OwnerAnalytics
 * //   POST   /api/marketplace/agents/:agentId/report      {category, note, conversationId?} → 201
 * //          409 when this reporter already has an OPEN report on this agent (AGM-123 dedupe)
 * //   POST   /api/marketplace/agents/:agentId/share-question {question, runId?} → 201
 * //   GET    /api/admin/marketplace/reports               → {items: AgentReport[]}
 * //   POST   /api/admin/marketplace/reports/:id/triage    {status} → 200
 * //   GET    /api/admin/mcp/curation-queue                → {items: CurationQueueItem[]}
 * //   PUT    /api/admin/mcp/capabilities/:capId/curation  CapabilityCuration → 200
 * //   DELETE /api/admin/mcp/capabilities/:capId/curation  → 200|204
 */

// ---------------------------------------------------------------------------
// Consumer plane
// ---------------------------------------------------------------------------

export type MessageFeedbackInput = { helpful: boolean; note?: string }

/**
 * Rates one finished answer.
 *
 * `note` is always sent, empty string included: the request schema is `.strict()` with a
 * defaulted note, so omitting the key and sending `''` are equivalent server-side, and
 * sending it always keeps the wire shape identical between the plain thumb and the thumb
 * with a note.
 *
 * // WIRE: the endpoint MUST upsert on (message, rater). The UI posts twice for one verdict
 * // — once when the thumb is pressed, again when a note is added to it — so an endpoint
 * // that inserted a row per call would double every tally the owner reads, and a person who
 * // changed their mind from helpful to not would count as both.
 */
export const sendMessageFeedback = (messageId: string, input: MessageFeedbackInput): Promise<void> =>
  apiSend<void>(`/api/messages/${encodeURIComponent(messageId)}/feedback`, 'POST', {
    helpful: input.helpful,
    note: input.note ?? ''
  })

export type ReportAgentInput = {
  category: 'impersonation' | 'harmful' | 'wrong' | 'inappropriate' | 'other'
  note?: string
  conversationId?: string
}

export const reportAgent = (agentId: string, input: ReportAgentInput): Promise<{ id: string }> =>
  apiSend<{ id: string }>(`/api/marketplace/agents/${encodeURIComponent(agentId)}/report`, 'POST', {
    category: input.category,
    note: input.note ?? '',
    ...(input.conversationId ? { conversationId: input.conversationId } : {})
  })

/**
 * A second report on an agent this person already reported (AGM-123 dedupe).
 *
 * Rendered as "you already reported this", not as an error: the report DID land the first
 * time, and an apology screen would invite the reporter to try again — which is exactly the
 * volume the dedupe exists to prevent.
 */
export const isDuplicateReport = (error: unknown): boolean => error instanceof ApiRequestError && error.status === 409

export type ShareQuestionInput = { question: string; runId?: string }

/**
 * Sends the user's own question to the agent's owner (AGM-120's improvement queue).
 *
 * Explicitly not the answer, and not the conversation: the owner learns what was asked so
 * they can teach the agent, and learns nothing about who asked or what came back. That the
 * question text is editable before sending is the consumer's control over exactly what
 * leaves their conversation.
 */
export const shareQuestion = (agentId: string, input: ShareQuestionInput): Promise<{ id: string }> =>
  apiSend<{ id: string }>(`/api/marketplace/agents/${encodeURIComponent(agentId)}/share-question`, 'POST', {
    question: input.question,
    ...(input.runId ? { runId: input.runId } : {})
  })

// ---------------------------------------------------------------------------
// Owner plane
// ---------------------------------------------------------------------------

export const fetchOwnerAnalytics = (agentId: string): Promise<OwnerAnalytics> =>
  apiGet<OwnerAnalytics>(`/api/maker/agents/${encodeURIComponent(agentId)}/analytics`)

export const fetchFeedbackSummary = (agentId: string): Promise<AgentFeedbackSummary> =>
  apiGet<AgentFeedbackSummary>(`/api/maker/agents/${encodeURIComponent(agentId)}/feedback`)

// ---------------------------------------------------------------------------
// Administrator plane
// ---------------------------------------------------------------------------

export const fetchAgentReports = (): Promise<{ items: AgentReport[] }> =>
  apiGet<{ items: AgentReport[] }>('/api/admin/marketplace/reports')

export const triageReport = (reportId: string, status: 'triaged' | 'dismissed'): Promise<{ status: string }> =>
  apiSend<{ status: string }>(`/api/admin/marketplace/reports/${encodeURIComponent(reportId)}/triage`, 'POST', {
    status
  })

export const fetchCurationQueue = (): Promise<{ items: CurationQueueItem[] }> =>
  apiGet<{ items: CurationQueueItem[] }>('/api/admin/mcp/curation-queue')

/**
 * Writes the words makers and consumers will read for one connected capability (AGM-080).
 *
 * PUT rather than POST because the write is the whole curation record and replaces whatever
 * was there — there is no partial curation, and a capability is either described in the
 * platform's own words or hidden from the maker plane entirely.
 */
export const saveCapabilityCuration = (capabilityId: string, curation: CapabilityCuration): Promise<unknown> =>
  api<unknown>(`/api/admin/mcp/capabilities/${encodeURIComponent(capabilityId)}/curation`, {
    method: 'PUT',
    body: JSON.stringify(curation)
  })

/** Withdrawing curated copy re-hides the capability from makers; existing grants are the API's problem, not this module's. */
export const clearCapabilityCuration = (capabilityId: string): Promise<unknown> =>
  apiSend<unknown>(`/api/admin/mcp/capabilities/${encodeURIComponent(capabilityId)}/curation`, 'DELETE')
